import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { oids } from "@/configs/Settings";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTeleconsultoriaContractContext } from "@/contexts/TeleconsultoriaContractContext";
import { useFormatter } from "@/utils/useFormatter";
import { createTeleconsultoriaSchedule } from "../services/createTeleconsultoriaSchedule";
import { patchHealthcareServiceActive } from "../services/patchHealthcareServiceActive";
import {
  fetchMyTeleconsultoria,
  organizationTokenFromId,
  setTeleconsultoriaScheduleActive,
} from "../services/teleconsultoriaScheduleService";
import { isTeleconsultoriaHealthcareService } from "../utils/actors";

/** Switch de disponibilidade. Disponível = Schedule.active da agenda de teleconsultoria. */
export function useTeleconsultoriaAvailability() {
  const { user, access } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { contractTeleconsultoria } = useTeleconsultoriaContractContext();
  const [schedule, setSchedule] = useState<fhir5.Schedule | null>(null);
  const [healthcareService, setHealthcareService] =
    useState<fhir5.HealthcareService | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const practitionerRef = `Practitioner/${oids.cpf}-${user.username}`;
  const practitionerDisplay = useFormatter.formatNameDisplay(user.name || "");
  const organizationToken = organizationTokenFromId(access?.organizationId);
  const specialtyName =
    userPractitionerRole?.specialty?.[0]?.coding?.[0]?.display ||
    "Teleconsultoria";

  const load = useCallback(async () => {
    if (!organizationToken || !user.username) return;
    setLoading(true);
    try {
      const found = await fetchMyTeleconsultoria({
        practitionerRef,
        organizationToken,
      });
      setSchedule(found.schedule || null);
      setHealthcareService(found.healthcareService || null);
    } catch (error) {
      console.error("useTeleconsultoriaAvailability.load", error);
      toast.error("Não foi possível carregar a teleconsultoria.");
      setSchedule(null);
      setHealthcareService(null);
    } finally {
      setLoading(false);
    }
  }, [organizationToken, practitionerRef, user.username]);

  useEffect(() => {
    void load();
  }, [load]);

  const setAvailable = async (available: boolean) => {
    if (!organizationToken) {
      toast.error("Organização não encontrada.");
      return;
    }
    // O Schedule só fica ativo enquanto a extensão da organização estiver habilitada.
    if (available && !contractTeleconsultoria) {
      toast.error("Ative a extensão Teleconsultoria para ficar disponível.");
      return;
    }
    setSaving(true);
    try {
      if (!schedule?.id) {
        if (!available) return;
        const created = await createTeleconsultoriaSchedule({
          practitionerRef,
          practitionerDisplay,
          organizationToken,
          specialtyName,
        });
        setSchedule(created);
        toast.success("Você está disponível para teleconsultoria.");
        return;
      }

      if ((schedule.active !== false) === available) return;
      // HCS marcado pode ter sido desativado no cancelamento da extensão.
      if (
        available &&
        healthcareService &&
        isTeleconsultoriaHealthcareService(healthcareService) &&
        healthcareService.active === false
      ) {
        await patchHealthcareServiceActive(healthcareService, true);
        setHealthcareService({ ...healthcareService, active: true });
      }
      await setTeleconsultoriaScheduleActive(schedule, available);
      setSchedule({ ...schedule, active: available });
      toast.success(
        available
          ? "Você está disponível para teleconsultoria."
          : "Teleconsultoria desativada.",
      );
    } catch (error) {
      console.error("useTeleconsultoriaAvailability.setAvailable", error);
      toast.error("Não foi possível atualizar a disponibilidade.");
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    available: schedule?.active !== false && Boolean(schedule?.id),
    setAvailable,
  };
}
